import { animated } from '@react-spring/web';
import clsx from 'clsx';

import AssignmentItem from 'src/components/assignments/assignment-item';
import { Cardstack } from 'src/components/assignments/cardstack';
import TaughtLessonAssignmentItem from 'src/components/assignments/taught-lesson-assignment-item';
import { ASSIGNMENT_COLORS } from 'src/constants/color.constants';
import { useAssignmentListAnimation } from 'src/hooks/use-assignment-list-animation';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { useMobileLayoutCheck } from 'src/hooks/use-mobile-layout-check';
import {
  Assignment,
  AssignmentsViewOptions,
} from 'src/interfaces/assignment.types';
import { useCurrentGradeSelector } from 'src/store/features';
import { getArrayItemByIndex } from 'src/utils/array.utils';

import './assignments-list.scss';

interface Props {
  assignments: Assignment[];
  k5Section?: AssignmentsViewOptions.PAST_WORK | AssignmentsViewOptions.TODO;
}

const AssignmentsList = ({ assignments, k5Section }: Props) => {
  const currentGrade = useCurrentGradeSelector();
  const isMiddleSchoolView = useIsMiddleSchoolView();

  const isMobileLayout = useMobileLayoutCheck();

  const [transitions, height] = useAssignmentListAnimation(
    assignments,
    isMiddleSchoolView,
    isMobileLayout,
  );

  const getContent = (assignment: Assignment, index: number) => {
    if (assignment.type === 'Cardstack') {
      return <Cardstack assignment={assignment} k5Section={k5Section} />;
    }

    if (assignment.type === 'TaughtLessonAssignment') {
      return (
        <TaughtLessonAssignmentItem
          taughtLesson={assignment}
          color={getArrayItemByIndex(index, ASSIGNMENT_COLORS)}
          grade={currentGrade}
          k5Section={k5Section}
        />
      );
    }

    return (
      <AssignmentItem
        assignment={assignment}
        color={getArrayItemByIndex(index, ASSIGNMENT_COLORS)}
        grade={currentGrade}
        k5Section={k5Section}
      />
    );
  };

  return (
    <section
      className={clsx('assignments-list', {
        'assignments-list--middle-school': isMiddleSchoolView,
      })}
      style={{ height }}
      data-testid="assignments-list"
    >
      {transitions((style, assignment, _, assignmentIndex) => (
        <animated.div
          className="assignment__card"
          data-testid="assignment-card"
          style={{ zIndex: assignments.length - assignmentIndex, ...style }}
        >
          {getContent(assignment, assignmentIndex)}
        </animated.div>
      ))}
    </section>
  );
};

export default AssignmentsList;
